'use client';

/**
 * @fileoverview `DiscoverHeroStripConnected` — client wrapper that renders the
 * Discover hero strip with the FRE-917 personalized `Discover, {name}.` title +
 * taste meta line layered on when the viewer is authenticated with a real taste
 * signal.
 *
 * The presentational {@link DiscoverHeroStrip} stays pure. This wrapper resolves
 * the gated personalization model via {@link useDiscoverPersonalization} (shared
 * discover subscription — no second fetch) and forwards `viewerName` +
 * `personalizedMeta` only when real data backs them. Anonymous / soaking viewers
 * receive neither, so the hero keeps the generic `Discover, {cityName}.` grammar
 * and the events/neighborhoods meta line — identical to the pre-FRE-917 render.
 *
 * @since FRE-917
 */
import * as React from 'react';

import type { CityCode } from '@loci/city-editions';

import { DiscoverHeroStrip } from '@/components/molecules/DiscoverHeroStrip';

import { useDiscoverEnvelopeTotals } from './useDiscoverEnvelopeTotals';
import { useDiscoverPersonalization } from './useDiscoverPersonalization';

export interface DiscoverHeroStripConnectedProps {
  /** Resolved city code — drives the personalization subscription cache key. */
  readonly cityCode: CityCode;
  /** City display name for the generic `Discover, {cityName}.` fallback. */
  readonly cityName: string;
  /**
   * SSR fallback for the in-range total. `undefined` until the page has
   * a payload — the connected wrapper then skeletons rather than painting 0.
   */
  readonly total?: number;
  /** SSR fallback for distinct neighborhoods (discover facets). */
  readonly neighborhoods?: number;
  /**
   * City index total (WEB-1187 A3). A finite count, including 0, wins over
   * the live facet count, which omits neighborhoods with nothing in the
   * window. `undefined` means the index could not be read.
   */
  readonly cityNeighborhoodCount?: number;
  /** Active time-window label for the generic meta line. */
  readonly window: string;
  /** Additional CSS classes. */
  readonly className?: string;
}

export function DiscoverHeroStripConnected({
  cityCode,
  cityName,
  total,
  neighborhoods,
  cityNeighborhoodCount,
  window,
  className,
}: DiscoverHeroStripConnectedProps): React.ReactElement {
  const personalization = useDiscoverPersonalization(cityCode);
  const totals = useDiscoverEnvelopeTotals(cityCode, {
    inRangeTotal: total,
    neighborhoodCount: neighborhoods,
  });

  // FRE-917 — `viewerName` gates ONLY the heading grammar. `personalizedMeta`
  // gates the taste meta line, and only when a measured corpus match count
  // exists so the hero cannot skeleton `MATCHES` forever on the API-1671
  // degrade path (WEB-786). A personalized viewer whose name we can't
  // honestly derive still gets the taste line when the count is present.
  const viewerName = personalization?.viewerName ?? undefined;
  // Taste framing needs a measured corpus match count. A resolved `null`
  // (API-1671 degrade) is honest absence — drop the MATCHES line and use
  // the in-range total so the hero cannot skeleton forever or disagree
  // with the results bar (WEB-786).
  const hasTasteCount = typeof totals.tasteMatchedTotal === 'number';
  const personalizedMeta =
    personalization && hasTasteCount
      ? {
          followedArtistCount: personalization.followedArtistCount,
          followedVenueCount: personalization.followedVenueCount,
        }
      : undefined;

  // WEB-786 — personalized "N MATCHES" is the corpus-wide taste-matched
  // total, not the in-range total and not a page-local tally. Pending
  // totals stay `null` so the molecule skeletons instead of painting 0.
  const headlineTotal = !totals.resolved
    ? null
    : personalization && hasTasteCount
      ? totals.tasteMatchedTotal
      : (totals.inRangeTotal ?? null);
  const headlineNeighborhoods =
    typeof cityNeighborhoodCount === 'number' && cityNeighborhoodCount >= 0
      ? cityNeighborhoodCount
      : (totals.neighborhoodCount ?? null);

  return (
    <DiscoverHeroStrip
      cityName={cityName}
      total={headlineTotal}
      neighborhoods={headlineNeighborhoods}
      window={window}
      viewerName={viewerName}
      personalizedMeta={personalizedMeta}
      className={className}
    />
  );
}
